import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { usePlayer } from '../context/PlayerContext';
import { toast } from 'sonner';
import FollowersModal from '../components/FollowersModal';
import { SubscribeButton } from '../components/SubscribeButton';
import { MessageButton } from '../components/MessageButton';
import { BellButton } from '../components/BellButton';
import { RequestButton } from '../components/RequestButton';
import { ChartRequestModal } from '../components/ChartRequestModal';
import { API } from '../config/api';
import PremiumArtistProfile from '../components/PremiumArtistProfile';
import ContentCreatorProfileView from '../components/ContentCreatorProfileView';
import EngineerProfileView from '../components/EngineerProfileView';
import StudioProfileView from '../components/StudioProfileView';
import VideographerProfileView from '../components/VideographerProfileView';
import DesignerProfileView from '../components/DesignerProfileView';
import PodcasterProfileView from '../components/PodcasterProfileView';
import StreamerProfileView from '../components/StreamerProfileView';

import { 
  Music, Mic2, Radio, Building2, Camera, Headphones, User,
  MapPin, Calendar, Play, Pause, Heart, Share2, Users, ExternalLink, 
  Verified, Loader2, UserPlus, UserMinus, Video, FileText,
  Instagram, Youtube, Twitter, Globe, MessageCircle, Eye,
  MoreHorizontal, Trash2, Lock, Clock, TrendingUp,
  Copy, Flag, Ban, Image, ZoomIn, Plus
} from 'lucide-react';
import { PremiumProfileHeader } from '../components/PremiumProfileHeader';
import { PhotoLightbox } from '../components/PhotoLightbox';
import { ListenerBadge } from '../components/ListenerBadge';
import { ListenerXPBar } from '../components/ListenerXPBar';
import { ListenerPhotoLibrary } from '../components/ListenerPhotoLibrary';
import { InfluenceProfileCard, TopSupportersCard } from '../components/InfluenceSystem';


// Social platform icons and styling
const SOCIAL_ICONS = {
  instagram: { icon: Instagram, color: 'text-pink-500', label: 'Instagram' },
  youtube: { icon: Youtube, color: 'text-red-500', label: 'YouTube' },
  twitter: { icon: Twitter, color: 'text-blue-400', label: 'X (Twitter)' },
  tiktok: { icon: Music, color: 'text-foreground', label: 'TikTok' },
  soundcloud: { icon: Music, color: 'text-orange-500', label: 'SoundCloud' },
  spotify: { icon: Music, color: 'text-green-500', label: 'Spotify' },
  website: { icon: Globe, color: 'text-foreground', label: 'Website' }
};

const ROLE_CONFIG = {
  listener: { icon: User, color: 'gray', label: 'Listener' },
  artist: { icon: Music, color: 'secondary', label: 'Artist' },
  content_creator: { icon: Video, color: 'violet', label: 'Content Creator' },
  podcaster: { icon: Mic2, color: 'green', label: 'Podcaster' },
  streamer: { icon: Radio, color: 'red', label: 'Streamer' },
  studio: { icon: Building2, color: 'amber', label: 'Recording Studio' },
  videographer: { icon: Camera, color: 'purple', label: 'Videographer' },
  engineer: { icon: Headphones, color: 'blue', label: 'Audio Engineer' },
  // NOTE: "owner" role displays as "Artist" publicly - Platform Owner badge is NOT shown publicly
  owner: { icon: Music, color: 'secondary', label: 'Artist' }
};

export default function PublicProfilePage() {
  const params = useParams();
  // Support both /u/:handle and /u/id/:userId routes
  const handle = params.handle;
  const userId = params.userId;
  const identifier = handle || userId;
  
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated, getAuthHeaders } = useAuth();
  
  // Profile state
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [profileUserId, setProfileUserId] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followStatus, setFollowStatus] = useState('not_following'); // not_following, following, request_pending
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  
  // Followers/Following modal state
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);
  
  // Raw role_profile data from API
  const [roleProfileData, setRoleProfileData] = useState(null);
  const [listenerData, setListenerData] = useState(null);
  
  // Content state - separate for each tab
  const [activeTab, setActiveTab] = useState('music');
  const [tracks, setTracks] = useState([]);
  const [videos, setVideos] = useState([]);
  const [posts, setPosts] = useState([]);
  const [replays, setReplays] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [photoLightbox, setPhotoLightbox] = useState({ open: false, index: 0 });
  const [loadingContent, setLoadingContent] = useState({
    tracks: false,
    videos: false,
    posts: false,
    replays: false,
    photos: false
  });
  
  // Chart request modal state
  const [chartRequestTrack, setChartRequestTrack] = useState(null);
  const [showChartRequestModal, setShowChartRequestModal] = useState(false);
  const [studioPlus, setStudioPlus] = useState(null);

  // Player for inline track playback
  const { playTrack, currentTrack, isPlaying, togglePlay } = usePlayer();

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds) || seconds <= 0) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayTrack = (track, e) => {
    if (e) e.stopPropagation();
    if (currentTrack?.track_id === track.track_id && isPlaying) {
      togglePlay();
    } else {
      playTrack(track, tracks);
    }
  };

  // Fetch profile on mount
  useEffect(() => {
    if (identifier && identifier !== 'undefined' && identifier !== 'null' && identifier.trim()) {
      console.log('[PublicProfile] Fetching profile for identifier:', identifier);
      fetchProfile();
    } else {
      console.warn('[PublicProfile] Invalid identifier:', identifier);
      navigate('/user-not-found');
    }
  }, [identifier]);

  // Fetch content when profile is loaded or tab changes
  useEffect(() => {
    if (profileUserId) {
      console.log('[PublicProfile] Profile user ID:', profileUserId);
      fetchContentForTab(activeTab);
    }
  }, [profileUserId, activeTab]);

  // Fetch Studio+ public customization settings so we can render a custom hero background
  useEffect(() => {
    if (!profileUserId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/studio-plus/public/${profileUserId}`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled && data?.is_studio_plus) setStudioPlus(data);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [profileUserId]);

  const fetchProfile = async () => {
    try {
      const cleanIdentifier = identifier.startsWith('@') ? identifier.slice(1) : identifier;
      console.log('[PublicProfile] Clean identifier:', cleanIdentifier);
      
      // Try unified user endpoint first (supports both handle and user_id)
      let res = await fetch(`${API}/user/${cleanIdentifier}`);
      
      if (!res.ok) {
        // Fallback to legacy profile endpoint
        res = await fetch(`${API}/profile/${cleanIdentifier}`);
      }
      
      if (!res.ok) {
        if (res.status === 404) {
          console.warn('[PublicProfile] User not found for:', cleanIdentifier);
          navigate(`/user-not-found/${cleanIdentifier}`);
          return;
        }
        throw new Error('Failed to load profile');
      }
      
      const data = await res.json();
      console.log('[PublicProfile] Profile data received:', data);
      
      const profileData = data.user || data.profile;
      
      if (!profileData || !profileData.user_id) {
        throw new Error('Invalid profile data');
      }
      
      const mappedProfile = {
        ...profileData,
        avatar_url: profileData.picture || profileData.avatar_url,
        socials: profileData.links || profileData.socials || {},
        has_artist_profile: profileData.role === 'artist' || profileData.role === 'owner',
        // Include artist_id from role_profile for subscription features
        artist_id: data.role_profile?.artist_id || profileData.artist_id,
        // Include artist_tier for premium profile rendering
        artist_tier: data.role_profile?.tier || profileData.artist_tier || profileData.tier || 'free',
        // Include banner and background URLs from role_profile (artist profile data)
        banner_url: data.role_profile?.banner_url || profileData.banner_url,
        background_url: data.role_profile?.background_url || profileData.background_url,
        // Genre and location from role_profile
        genre: data.role_profile?.genres?.[0] || profileData.genre,
        primary_genre: data.role_profile?.genres?.[0] || profileData.primary_genre,
        genres: data.role_profile?.genres || profileData.genres || [],
        neighborhood: data.role_profile?.neighborhood || profileData.neighborhood,
        city: data.role_profile?.city || profileData.city,
        location: data.role_profile?.location || profileData.location,
        bio: data.role_profile?.bio || profileData.bio,
        stage_name: data.role_profile?.stage_name || profileData.stage_name,
        // Privacy fields - content_accessible is explicitly true only when set
        is_private: profileData.is_private || false,
        content_accessible: profileData.content_accessible === true,
        privacy_message: data.privacy_message || null
      };
      
      setProfile(mappedProfile);
      setProfileUserId(profileData.user_id);
      setRoleProfileData(data.role_profile || null);
      setFollowerCount(data.user?.followers_count || data.follower_count || profileData.followers_count || 0);
      setFollowingCount(data.user?.following_count || profileData.following_count || 0);
      setIsFollowing(data.is_following || false);
      
      // Record profile view (fire-and-forget)
      fetch(`${API}/users/${profileData.user_id}/profile-view`, {
        method: 'POST',
        headers: getAuthHeaders()
      }).catch(() => {});
      
      // Fetch detailed follow status for private account handling
      if (profileData.user_id) {
        try {
          const statusRes = await fetch(`${API}/users/${profileData.user_id}/follow-status`, {
            headers: getAuthHeaders()
          });
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            setFollowStatus(statusData.status);
            setIsFollowing(statusData.is_following);
          }
        } catch (e) {
          console.log('[PublicProfile] Follow status check failed (may not be logged in)');
        }
      }
      
      console.log('[PublicProfile] Set profileUserId to:', profileData.user_id, 'is_private:', mappedProfile.is_private);

      // Fetch listener profile data (for badge display)
      fetch(`${API}/listener/profile/${profileData.user_id}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setListenerData(d); })
        .catch(() => {});
    } catch (err) {
      console.error('[PublicProfile] Profile fetch error:', err);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchContentForTab = async (tab) => {
    if (!profileUserId) {
      console.warn('[PublicProfile] No profileUserId, skipping content fetch');
      return;
    }
    
    // Don't fetch content for private profiles (unless we have access)
    if (profile?.is_private && !profile?.content_accessible) {
      console.log('[PublicProfile] Private profile, skipping content fetch');
      return;
    }
    
    console.log(`[PublicProfile] Fetching ${tab} for user:`, profileUserId);
    
    setLoadingContent(prev => ({ ...prev, [tab]: true }));
    
    try {
      const headers = isAuthenticated ? getAuthHeaders() : {};
      
      switch (tab) {
        case 'music':
          // Fetch tracks for this profile user
          const tracksRes = await fetch(`${API}/tracks?user_id=${profileUserId}&limit=50`, { headers });
          if (tracksRes.ok) {
            const tracksData = await tracksRes.json();
            // Backend returns array directly, not wrapped in { tracks: [...] }
            const tracksArray = Array.isArray(tracksData) ? tracksData : (tracksData.tracks || []);
            console.log('[PublicProfile] Tracks received:', tracksArray.length);
            setTracks(tracksArray);
          }
          break;
          
        case 'videos':
          // Fetch videos for this profile user
          const videosRes = await fetch(`${API}/videos?user_id=${profileUserId}&limit=50`, { headers });
          if (videosRes.ok) {
            const videosData = await videosRes.json();
            // Backend returns array directly, not wrapped in { videos: [...] }
            const videosArray = Array.isArray(videosData) ? videosData : (videosData.videos || []);
            console.log('[PublicProfile] Videos received:', videosArray.length);
            setVideos(videosArray);
          }
          break;
          
        case 'posts':
          // Fetch posts for this profile user - CRITICAL: use profileUserId NOT currentUser
          const postsRes = await fetch(`${API}/posts/user/${profileUserId}?visibility=public&limit=50`, { headers });
          if (postsRes.ok) {
            const postsData = await postsRes.json();
            console.log('[PublicProfile] Posts received:', postsData.posts?.length || 0);
            setPosts(postsData.posts || []);
          } else {
            // Fallback: try artist posts endpoint (returns array directly)
            const artistPostsRes = await fetch(`${API}/artists/${profileUserId}/posts?limit=50`, { headers });
            if (artistPostsRes.ok) {
              const artistPostsData = await artistPostsRes.json();
              const postsArray = Array.isArray(artistPostsData) ? artistPostsData : (artistPostsData.posts || []);
              console.log('[PublicProfile] Artist posts received:', postsArray.length);
              setPosts(postsArray);
            }
          }
          break;
          
        case 'replays':
          const replaysRes = await fetch(`${API}/live/replays/user/${profileUserId}?limit=20`, { headers });
          if (replaysRes.ok) {
            const replaysData = await replaysRes.json();
            setReplays(replaysData.replays || []);
          }
          break;

        case 'photos':
          const photosRes = await fetch(`${API}/users/${profileUserId}/photos`, { headers });
          if (photosRes.ok) {
            const photosData = await photosRes.json();
            setPhotos(photosData.photos || []);
          }
          break;
      }
    } catch (err) {
      console.error(`[PublicProfile] Error fetching ${tab}:`, err);
    } finally {
      setLoadingContent(prev => ({ ...prev, [tab]: false }));
    }
  };

  const handleFollow = async () => {
    if (!isAuthenticated) {
      toast.info('Sign in to follow');
      navigate('/login');
      return;
    }
    
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      
      // Handle different states
      if (followStatus === 'request_pending') {
        // Cancel pending request
        const res = await fetch(`${API}/users/${profile.user_id}/cancel-follow-request`, {
          method: 'POST',
          headers
        });
        
        if (res.ok) {
          setFollowStatus('not_following');
          toast.success('Follow request cancelled');
        }
      } else if (isFollowing) {
        // Unfollow
        const res = await fetch(`${API}/users/${profile.user_id}/unfollow`, {
          method: 'POST',
          headers
        });
        
        if (res.ok) {
          setIsFollowing(false);
          setFollowStatus('not_following');
          setFollowerCount(prev => prev - 1);
          toast.success('Unfollowed');
        }
      } else {
        // Follow (or request for private accounts)
        const res = await fetch(`${API}/users/${profile.user_id}/follow`, {
          method: 'POST',
          headers
        });
        
        if (res.ok) {
          const data = await res.json();
          
          if (data.status === 'request_sent') {
            setFollowStatus('request_pending');
            toast.success('Follow request sent');
          } else if (data.status === 'following' || data.status === 'already_following') {
            setIsFollowing(true);
            setFollowStatus('following');
            setFollowerCount(prev => prev + 1);
            toast.success('Following');
          }
        }
      }
    } catch (err) {
      toast.error('Failed to update follow');
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: profile.name,
          url: shareUrl
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied!');
      }
    } catch (error) {
      // AbortError = user cancelled share, this is fine - do nothing
      if (error.name === 'AbortError' || error.message?.includes('cancel')) {
        return;
      }
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied!');
      } catch {
        console.error('Share failed:', error);
      }
    }
  };

  const handleDeleteTrack = async (trackId, trackTitle) => {
    if (!isAuthenticated) {
      toast.error('You must be logged in to delete tracks');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${trackTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API}/tracks/${trackId}`, {
        method: 'DELETE',
        headers
      });

      if (res.ok) {
        toast.success('Track deleted successfully');
        // Remove from local state
        setTracks(prev => prev.filter(t => t.track_id !== trackId));
      } else {
        const data = await res.json();
        toast.error(data?.detail || 'Failed to delete track');
      }
    } catch (error) {
      console.error('Delete track error:', error);
      toast.error('Failed to delete track');
    }
  };

  const handleLikePost = async (postId) => {
    if (!isAuthenticated) {
      toast.info('Sign in to like posts');
      navigate('/login');
      return;
    }

    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      
      // Find the post to check if already liked
      const post = posts.find(p => p.post_id === postId);
      const isLiked = post?.liked_by_me;
      
      const endpoint = isLiked ? 'unlike' : 'like';
      await fetch(`${API}/posts/${postId}/${endpoint}`, {
        method: 'POST',
        headers
      });
      
      // Update local state
      setPosts(prev => prev.map(p => {
        if (p.post_id === postId) {
          return {
            ...p,
            liked_by_me: !isLiked,
            likes: isLiked ? (p.likes || 1) - 1 : (p.likes || 0) + 1,
            likes_count: isLiked ? (p.likes_count || 1) - 1 : (p.likes_count || 0) + 1
          };
        }
        return p;
      }));
    } catch (err) {
      console.error('Like error:', err);
      toast.error('Failed to like post');
    }
  };

  const _buildGalleryImages = () => {
    const images = [];
    if (profile?.avatar_url) {
      images.push(profile.avatar_url.startsWith('http') ? profile.avatar_url : `${API.replace('/api', '')}${profile.avatar_url}`);
    }
    if (profile?.banner_url) {
      images.push(profile.banner_url.startsWith('http') ? profile.banner_url : `${API.replace('/api', '')}${profile.banner_url}`);
    }
    photos.forEach(p => {
      const url = p.photo_url || p.image_url;
      if (url) images.push(url.startsWith('http') ? url : `${API.replace('/api', '')}${url}`);
    });
    return images;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <User className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">User Not Found</h1>
          <p className="text-muted-foreground mb-4">The profile you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  const roleConfig = ROLE_CONFIG[profile.role] || ROLE_CONFIG.listener;
  const RoleIcon = roleConfig.icon;
  const isOwnProfile = currentUser?.user_id === profile.user_id;
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'owner';
  const isCreator = ['artist', 'owner', 'content_creator', 'podcaster', 'streamer', 'videographer', 'producer', 'engineer', 'studio'].includes(profile.role);
  
  // Check if this is an artist profile — ALL artists get the full artist layout
  const isArtistProfile = profile.has_artist_profile || profile.role === 'artist';

  // Studio+ custom background layer — rendered above ANY role-based profile view
  const studioPlusBg = studioPlus?.settings?.background ? (
    <>
      {studioPlus.settings.background.type === 'video' && studioPlus.settings.background.value ? (
        <video
          src={studioPlus.settings.background.value}
          autoPlay loop muted playsInline
          className="fixed inset-0 w-full h-full object-cover pointer-events-none"
          style={{ zIndex: 0 }}
          data-testid="studio-plus-bg-video"
        />
      ) : (
        <div
          className="fixed inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
          style={{
            zIndex: 0,
            background: studioPlus.settings.background.type === 'gradient'
              ? studioPlus.settings.background.value
              : studioPlus.settings.background.type === 'image' && studioPlus.settings.background.value
                ? `url(${studioPlus.settings.background.value}) center / cover no-repeat`
                : undefined,
          }}
          data-testid="studio-plus-bg-static"
        />
      )}
      <div className="fixed inset-0 bg-gradient-to-b from-black/30 via-black/50 to-background/95 pointer-events-none" style={{ zIndex: 1 }} />
    </>
  ) : null;

  // Render Content Creator Profile (by role or profile_mode) — checked FIRST, profile_mode overrides tier layout
  if (profile.role === 'content_creator' || profile.profile_mode === 'content_creator') {
    return (
      <>
        {studioPlusBg}
        <ContentCreatorProfileView
          profile={profile}
          isOwnProfile={isOwnProfile}
          isFollowing={isFollowing}
          onFollow={handleFollow}
          followerCount={followerCount}
          followingCount={followingCount}
          onShowFollowers={() => setShowFollowersModal(true)}
          onShowFollowing={() => setShowFollowingModal(true)}
        />
        <FollowersModal isOpen={showFollowersModal} onClose={() => setShowFollowersModal(false)} userId={profileUserId} type="followers" userName={profile?.handle} />
        <FollowersModal isOpen={showFollowingModal} onClose={() => setShowFollowingModal(false)} userId={profileUserId} type="following" userName={profile?.handle} />
      </>
    );
  }

  // Render Artist Profile for ALL artists (any tier)
  if (isArtistProfile) {
    return (
      <>
        {studioPlusBg}
        <PremiumArtistProfile
          profile={profile}
          isOwnProfile={isOwnProfile}
          isFollowing={isFollowing}
          onFollow={handleFollow}
          followerCount={followerCount}
          followingCount={followingCount}
          onShowFollowers={() => setShowFollowersModal(true)}
          onShowFollowing={() => setShowFollowingModal(true)}
        />
        
        {/* Followers Modal */}
        <FollowersModal
          isOpen={showFollowersModal}
          onClose={() => setShowFollowersModal(false)}
          userId={profileUserId}
          type="followers"
          userName={profile?.handle}
        />
        
        {/* Following Modal */}
        <FollowersModal
          isOpen={showFollowingModal}
          onClose={() => setShowFollowingModal(false)}
          userId={profileUserId}
          type="following"
          userName={profile?.handle}
        />
      </>
    );
  }

  // Render Engineer Profile
  if (profile.role === 'engineer') {
    return (
      <>
        {studioPlusBg}
        <EngineerProfileView
          profile={profile}
          roleProfile={roleProfileData}
          isOwnProfile={isOwnProfile}
          followerCount={followerCount}
          followingCount={followingCount}
          isFollowing={isFollowing}
          onFollowChange={(delta) => setFollowerCount(prev => prev + delta)}
        />
      </>
    );
  }

  // Render Studio Profile
  if (profile.role === 'studio') {
    return (
      <>
        {studioPlusBg}
        <StudioProfileView
          profile={profile}
          roleProfile={roleProfileData}
          isOwnProfile={isOwnProfile}
          followerCount={followerCount}
          followingCount={followingCount}
          isFollowing={isFollowing}
          onFollowChange={(delta) => setFollowerCount(prev => prev + delta)}
        />
      </>
    );
  }

  // Render Videographer Profile
  if (profile.role === 'videographer') {
    return (
      <>
        {studioPlusBg}
        <VideographerProfileView
          profile={profile}
          roleProfile={roleProfileData}
          isOwnProfile={isOwnProfile}
          followerCount={followerCount}
          followingCount={followingCount}
          isFollowing={isFollowing}
          onFollowChange={(delta) => setFollowerCount(prev => prev + delta)}
        />
      </>
    );
  }

  // Render Graphic Designer Profile
  if (profile.role === 'graphic_designer') {
    return (
      <>
        {studioPlusBg}
        <DesignerProfileView
          profile={profile}
          roleProfile={roleProfileData}
          isOwnProfile={isOwnProfile}
          followerCount={followerCount}
          followingCount={followingCount}
          isFollowing={isFollowing}
          onFollowChange={(delta) => setFollowerCount(prev => prev + delta)}
        />
      </>
    );
  }

  // Render Podcaster Profile
  if (profile.role === 'podcaster') {
    return (
      <>
        {studioPlusBg}
        <PodcasterProfileView
          profile={profile}
          roleProfile={roleProfileData}
          isOwnProfile={isOwnProfile}
          followerCount={followerCount}
          followingCount={followingCount}
          isFollowing={isFollowing}
          onFollowChange={(delta) => setFollowerCount(prev => prev + delta)}
        />
      </>
    );
  }

  // Render Streamer Profile
  if (profile.role === 'streamer') {
    return (
      <>
        {studioPlusBg}
        <StreamerProfileView
          profile={profile}
          roleProfile={roleProfileData}
          isOwnProfile={isOwnProfile}
          followerCount={followerCount}
          followingCount={followingCount}
          isFollowing={isFollowing}
          onFollowChange={(delta) => setFollowerCount(prev => prev + delta)}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen relative" data-testid="public-profile-page">
      {/* Studio+ custom background (overrides default when active) */}
      {studioPlusBg}

      {/* Full Page Background (for artists with background_url) — hidden when Studio+ background is active */}
      {profile.background_url && !studioPlus?.settings?.background && (
        <>
          <div 
            className="fixed inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: `url(${profile.background_url.startsWith('http') ? profile.background_url : `${API.replace('/api', '')}${profile.background_url}`})`,
              zIndex: 0
            }}
          />
          <div className="fixed inset-0 bg-gradient-to-b from-black/40 via-black/50 to-background/90" style={{ zIndex: 1 }} />
        </>
      )}
      
      {/* Content wrapper */}
      <div className="relative" style={{ zIndex: 10 }}>
        {/* Premium Profile Header */}
        <PremiumProfileHeader
          profile={profile}
          roleProfileData={roleProfileData}
          isOwnProfile={isOwnProfile}
          isFollowing={isFollowing}
          followStatus={followStatus}
          followerCount={followerCount}
          followingCount={followingCount}
          tracks={tracks}
          onFollow={handleFollow}
          onShare={handleShare}
          onShowFollowers={() => setShowFollowersModal(true)}
          onShowFollowing={() => setShowFollowingModal(true)}
          listenerData={listenerData}
        />

        <div className="max-w-4xl mx-auto px-4">

        {/* Private Profile Notice */}
        {profile.is_private && !profile.content_accessible && (
          <Card className="glass-card border-border p-8 mt-8 text-center" data-testid="private-profile-notice">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-2">This Account is Private</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Follow {profile.name} to see their posts, music, videos, and more.
            </p>
            {!isOwnProfile && (
              <Button 
                onClick={handleFollow}
                className="bg-primary hover:bg-primary/90"
                data-testid="follow-private-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {isFollowing ? 'Following' : 'Follow'}
              </Button>
            )}
          </Card>
        )}

        {/* Influence System */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <InfluenceProfileCard userId={profileUserId} getAuthHeaders={getAuthHeaders} />
          {isCreator && <TopSupportersCard creatorId={profileUserId} />}
        </div>

        {/* Content Tabs: Music / Videos / Posts - Only show if content is accessible */}
        {(!profile.is_private || profile.content_accessible) && (
        <div className="mt-8 pb-12">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full justify-start bg-muted/30 p-1 mb-6 overflow-x-auto flex-nowrap">
              {isCreator && (
                <TabsTrigger value="music" className="flex items-center gap-2 shrink-0" data-testid="tab-music">
                  <Music className="w-4 h-4" />
                  Music
                  {tracks.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{tracks.length}</Badge>}
                </TabsTrigger>
              )}
              {isCreator && (
                <TabsTrigger value="videos" className="flex items-center gap-2 shrink-0" data-testid="tab-videos">
                  <Video className="w-4 h-4" />
                  Videos
                  {videos.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{videos.length}</Badge>}
                </TabsTrigger>
              )}
              <TabsTrigger value="posts" className="flex items-center gap-2 shrink-0" data-testid="tab-posts">
                <FileText className="w-4 h-4" />
                Posts
                {posts.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{posts.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="replays" className="flex items-center gap-2 shrink-0" data-testid="tab-replays">
                <Radio className="w-4 h-4" />
                Replays
              </TabsTrigger>
              <TabsTrigger value="photos" className="flex items-center gap-2 shrink-0" data-testid="tab-photos">
                <Image className="w-4 h-4" />
                Photos
              </TabsTrigger>
            </TabsList>

            {/* Posts Tab */}
            <TabsContent value="posts">
              {loadingContent.posts ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : posts.length === 0 ? (
                <Card className="glass-card border-border p-12 text-center">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-medium text-lg mb-2">No posts yet</h3>
                  <p className="text-muted-foreground">
                    {isOwnProfile ? "Share your first update with your followers!" : `${profile.name} hasn't posted anything yet.`}
                  </p>
                  {isOwnProfile && (
                    <Button className="mt-4" onClick={() => navigate('/dashboard')}>
                      Create Post
                    </Button>
                  )}
                </Card>
              ) : (
                <div className="space-y-4">
                  {posts.map((post) => (
                    <Card key={post.post_id} className="glass-card border-border p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-muted/50 overflow-hidden flex-shrink-0">
                          {profile.avatar_url ? (
                            <img src={profile.avatar_url.startsWith('http') ? profile.avatar_url : `${API.replace('/api', '')}${profile.avatar_url}`} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-5 h-5 m-2.5 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{profile.name}</span>
                            <span className="text-muted-foreground text-sm">{profile.handle ? `@${profile.handle}` : ""}</span>
                            <span className="text-muted-foreground text-xs">•</span>
                            <span className="text-muted-foreground text-xs">
                              {new Date(post.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          
                          <p className="text-sm mb-3 whitespace-pre-wrap">
                            {(post.content_text || post.content || '').split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
                              /^https?:\/\//.test(part) ? (
                                <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">{part}</a>
                              ) : part
                            )}
                          </p>
                          
                          {/* Post Media */}
                          {(post.content_image_url || post.content_video_url || (post.media_urls && post.media_urls.length > 0)) && (
                            <div className="rounded-lg overflow-hidden mb-3">
                              {post.content_video_url ? (
                                <video 
                                  src={post.content_video_url.startsWith('http') ? post.content_video_url : `${API.replace('/api', '')}${post.content_video_url}`}
                                  controls
                                  className="w-full max-h-96 object-contain bg-black"
                                />
                              ) : post.content_image_url ? (
                                post.youtube_url ? (
                                  <a href={post.youtube_url} target="_blank" rel="noopener noreferrer" className="block relative group">
                                    <img 
                                      src={post.content_image_url.startsWith('http') ? post.content_image_url : `${API.replace('/api', '')}${post.content_image_url}`}
                                      alt="" 
                                      className="w-full max-h-96 object-contain"
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                                      <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center shadow-lg">
                                        <svg className="w-7 h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                      </div>
                                    </div>
                                  </a>
                                ) : (
                                  <img 
                                    src={post.content_image_url.startsWith('http') ? post.content_image_url : `${API.replace('/api', '')}${post.content_image_url}`}
                                    alt="" 
                                    className="w-full max-h-96 object-contain"
                                  />
                                )
                              ) : post.media_urls?.[0] && (
                                <img 
                                  src={post.media_urls[0].startsWith('http') ? post.media_urls[0] : `${API.replace('/api', '')}${post.media_urls[0]}`}
                                  alt="" 
                                  className="w-full max-h-96 object-contain"
                                />
                              )}
                            </div>
                          )}
                          
                          {/* Post Actions */}
                          <div className="flex items-center gap-6 text-muted-foreground">
                            <button 
                              className="flex items-center gap-1.5 hover:text-red-400 transition-colors"
                              onClick={() => handleLikePost(post.post_id)}
                              data-testid={`like-btn-${post.post_id}`}
                            >
                              <Heart className={`w-4 h-4 ${post.liked_by_me ? 'fill-red-500 text-red-500' : ''}`} />
                              <span className="text-sm">{post.likes || post.likes_count || 0}</span>
                            </button>
                            <button 
                              className="flex items-center gap-1.5 hover:text-secondary transition-colors"
                              onClick={() => navigate(`/post/${post.post_id}`)}
                              data-testid={`comment-btn-${post.post_id}`}
                            >
                              <MessageCircle className="w-4 h-4" />
                              <span className="text-sm">{post.comments_count || 0}</span>
                            </button>
                            <button className="flex items-center gap-1.5 hover:text-secondary transition-colors">
                              <Eye className="w-4 h-4" />
                              <span className="text-sm">{post.views || 0}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Music Tab */}
            <TabsContent value="music">
              {loadingContent.tracks ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : tracks.length === 0 ? (
                <Card className="glass-card border-border p-12 text-center">
                  <Music className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-medium text-lg mb-2">No music yet</h3>
                  <p className="text-muted-foreground">
                    {isOwnProfile ? "Upload your first track!" : `${profile.name} hasn't uploaded any music yet.`}
                  </p>
                  {isOwnProfile && (
                    <Button className="mt-4" onClick={() => navigate('/dashboard/artist')}>
                      Upload Track
                    </Button>
                  )}
                </Card>
              ) : (
                <div className="space-y-1" data-testid="music-track-list">
                  {tracks.map((track, index) => {
                    const isCurrentlyPlaying = currentTrack?.track_id === track.track_id && isPlaying;
                    const dur = formatDuration(track.duration);
                    return (
                      <div
                        key={track.track_id}
                        className={`group flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer ${
                          isCurrentlyPlaying ? 'bg-secondary/10 border border-secondary/20' : 'hover:bg-muted/25 border border-transparent'
                        }`}
                        data-testid={`track-card-${track.track_id}`}
                      >
                        {/* Track Number / Play Button */}
                        <div className="w-8 flex-shrink-0 flex items-center justify-center">
                          <button
                            onClick={(e) => handlePlayTrack(track, e)}
                            className="w-8 h-8 rounded-full flex items-center justify-center transition-all"
                            data-testid={`play-btn-${track.track_id}`}
                          >
                            {isCurrentlyPlaying ? (
                              <Pause className="w-4 h-4 text-secondary" />
                            ) : (
                              <>
                                <span className="group-hover:hidden text-sm text-muted-foreground">{index + 1}</span>
                                <Play className="w-4 h-4 text-secondary hidden group-hover:block ml-0.5" />
                              </>
                            )}
                          </button>
                        </div>

                        {/* Cover Art */}
                        <div className="w-12 h-12 rounded-lg bg-muted/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {track.cover_art_url || track.cover_url ? (
                            <img src={track.cover_art_url || track.cover_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Music className="w-5 h-5 text-secondary/50" />
                          )}
                        </div>

                        {/* Track Info */}
                        <div className="flex-1 min-w-0" onClick={() => navigate(`/track/${track.track_id}`)}>
                          <p className={`font-medium text-sm truncate ${isCurrentlyPlaying ? 'text-secondary' : ''}`}>
                            {track.title}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {track.primary_genre_name || track.genre || track.primary_genre || ''}
                            {track.explicit && <span className="ml-1.5 px-1 py-0.5 bg-muted/50 rounded text-[10px] font-bold">E</span>}
                          </p>
                        </div>

                        {/* Request Button (per-track) */}
                        {!isOwnProfile && (
                          <div className="flex-shrink-0 hidden sm:block">
                            <RequestButton
                              targetType="song"
                              targetId={track.track_id}
                              targetName={track.title}
                              creatorName={track.artist_name || profile.name}
                              variant="ghost"
                              size="sm"
                              className="text-xs text-muted-foreground hover:text-secondary"
                            />
                          </div>
                        )}

                        {/* Stats */}
                        <div className="hidden md:flex items-center gap-4 text-xs text-muted-foreground flex-shrink-0">
                          <span className="flex items-center gap-1" data-testid={`plays-${track.track_id}`}>
                            <Play className="w-3 h-3" /> {track.plays || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="w-3 h-3" /> {track.likes || 0}
                          </span>
                        </div>

                        {/* Duration */}
                        {dur && (
                          <span className="text-xs text-muted-foreground flex-shrink-0 w-10 text-right" data-testid={`duration-${track.track_id}`}>
                            {dur}
                          </span>
                        )}

                        {/* Track Menu */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="flex-shrink-0 w-8 h-8 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`track-menu-${track.track_id}`}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setChartRequestTrack(track);
                                setShowChartRequestModal(true);
                              }}
                              data-testid={`request-top20-${track.track_id}`}
                            >
                              <TrendingUp className="w-4 h-4 mr-2 text-secondary" />
                              Request Top 20
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/track/${track.track_id}`)}>
                              <ExternalLink className="w-4 h-4 mr-2" />
                              View Track
                            </DropdownMenuItem>
                            {(isOwnProfile || isAdmin) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-500"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTrack(track.track_id, track.title);
                                  }}
                                  data-testid={`delete-track-${track.track_id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Track
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Videos Tab */}
            <TabsContent value="videos">
              {loadingContent.videos ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : videos.length === 0 ? (
                <Card className="glass-card border-border p-12 text-center">
                  <Video className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-medium text-lg mb-2">No videos yet</h3>
                  <p className="text-muted-foreground">
                    {isOwnProfile ? "Upload your first video!" : `${profile.name} hasn't uploaded any videos yet.`}
                  </p>
                  {isOwnProfile && (
                    <Button className="mt-4" onClick={() => navigate('/dashboard')}>
                      Upload Video
                    </Button>
                  )}
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {videos.map((video) => (
                    <Card 
                      key={video.video_id} 
                      className="glass-card border-border overflow-hidden hover:border-border transition-colors cursor-pointer"
                      onClick={() => navigate(`/video/${video.video_id}`)}
                    >
                      <div className="aspect-video bg-black relative">
                        {video.thumbnail_url ? (
                          <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Video className="w-12 h-12 text-muted-foreground" />
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity">
                          <Play className="w-12 h-12 text-white" />
                        </div>
                        {video.duration && (
                          <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-background/70 rounded text-xs">
                            {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                          </span>
                        )}
                      </div>
                      <div className="p-3">
                        <p className="font-medium truncate">{video.title}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                          <span>{video.views || 0} views</span>
                          <span>{new Date(video.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Replays Tab */}
            <TabsContent value="replays">
              {loadingContent.replays ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : replays.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Radio className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No live replays yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {replays.map(replay => (
                    <Card
                      key={replay.replay_id}
                      className="overflow-hidden hover:ring-1 hover:ring-primary/30 transition-all cursor-pointer"
                      onClick={() => navigate(`/live/replay/${replay.replay_id}`)}
                      data-testid={`replay-${replay.replay_id}`}
                    >
                      <div className="relative aspect-video bg-muted">
                        {replay.cover_image ? (
                          <img src={replay.cover_image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Radio className="w-10 h-10 text-muted-foreground/30" />
                          </div>
                        )}
                        {replay.duration_seconds && (
                          <span className="absolute bottom-2 right-2 bg-background/80 text-foreground text-xs px-2 py-0.5 rounded">
                            {Math.floor(replay.duration_seconds / 60)}:{String(replay.duration_seconds % 60).padStart(2, '0')}
                          </span>
                        )}
                        <div className="absolute top-2 left-2">
                          <Badge variant="secondary" className="bg-red-600/90 text-foreground text-xs">REPLAY</Badge>
                        </div>
                      </div>
                      <div className="p-3">
                        <h4 className="font-semibold text-sm truncate">{replay.title || 'Live Replay'}</h4>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                          <span>{replay.viewer_count || 0} viewers</span>
                          <span>{replay.views || 0} views</span>
                          <span>{new Date(replay.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Photos Tab */}
            <TabsContent value="photos">
              <ListenerPhotoLibrary
                userId={profileUserId}
                isOwnProfile={isOwnProfile}
                getAuthHeaders={getAuthHeaders}
                profile={profile}
              />
            </TabsContent>
          </Tabs>
        </div>
        )}
      </div>
      </div>
      
      {/* Photo Lightbox */}
      {photoLightbox.open && (
        <PhotoLightbox
          images={_buildGalleryImages()}
          initialIndex={photoLightbox.index}
          onClose={() => setPhotoLightbox({ open: false, index: 0 })}
        />
      )}
      
      {/* Followers Modal */}
      <FollowersModal
        isOpen={showFollowersModal}
        onClose={() => setShowFollowersModal(false)}
        userId={profileUserId}
        type="followers"
        userName={profile?.handle}
      />
      
      {/* Following Modal */}
      <FollowersModal
        isOpen={showFollowingModal}
        onClose={() => setShowFollowingModal(false)}
        userId={profileUserId}
        type="following"
        userName={profile?.handle}
      />
      
      {/* Chart Request Modal */}
      <ChartRequestModal
        isOpen={showChartRequestModal}
        onClose={() => {
          setShowChartRequestModal(false);
          setChartRequestTrack(null);
        }}
        track={chartRequestTrack}
      />
    </div>
  );
}
